import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { AppConfig } from '../../utils/app-config.service';
import {
    ClientInterface,
    ContractInterface,
    DisinvestmentInterface,
    FeeInterface,
    FileInterface,
    IdInterface,
    ModelInterface,
    OrderClientInterface,
    TransferInterface,
    ValidModelInterface,
} from '../../../core';
import { File, Model, ValidModel } from '../../domain/data';
import { MyHttpClient } from './my-http-client';
import { Serializer } from '../helpers/serializer/serializer';

import { mapCommonResponse } from './api-client.helpers';

import { environment } from '../../../../environments/environment';
import { LocalStorage } from '../helpers/local-storage';

@Injectable()
export class TransferApiClient {
    constructor(private http: MyHttpClient, private serializer: Serializer,
        private localStorage: LocalStorage,
    ) {}

    evaluateFee(contractCode: IdInterface, fee: FeeInterface): Observable<number> {
        return of(AppConfig.settings.services.base + environment.services.paths.evaluate_fee).pipe(
            switchMap((url) => {
                const body = this.serializer.toJson(fee);
                body.PortfolioCode = String(contractCode);

                return this.http.post(url, body).pipe(map((resp: any) => Number(resp.FeedAmount)));
            })
        );
    }

    getModel(id: IdInterface): Observable<ModelInterface> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_model).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        ModelPortfolioId: String(id),
                    })
                    .pipe(
                        map((resp: any) => {
                            if (!resp || (resp.Status !== 'OK' && resp.Status !== 'ok' && resp.Status !== '')) {
                                throw new Error(resp ? resp.Status : 'error.general');
                            } else {
                                localStorage.setItem('CurrentModelName', resp.ModelPortfolioDetails.Name);
                                return this.serializer.fromJson(resp.ModelPortfolioDetails, Model);
                            }
                        })
                    )
            )
        );
    }

    getModelPdf(id: IdInterface): Observable<FileInterface> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_model_pdf).pipe(
            switchMap((url) =>
                this.http
                    .post(
                        url,
                        {
                            ModelPortfolioId: String(id),
                        },
                        { observe: 'response', responseType: 'text' }
                    )
                    .pipe(
                        map((resp: HttpResponse<any>) => {
                            const fileName = resp.headers
                                .get('Content-Disposition')
                                .split(' ')
                                .find((it) => it.startsWith('filename='))
                                .replace('filename=', '');
                            return new File(resp.body, fileName);
                        })
                    )
            )
        );
    }

    getValidModels(contract: ContractInterface, client: ClientInterface): Observable<ValidModelInterface[]> {
        return of(AppConfig.settings.services.base + environment.services.paths.get_valid_model).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        AdvisoryModelCode: contract.advisoryModel,
                        PortfolioCode: String(contract.encryptedCode),
                        ClientId: String(client.id),
                        ProfileId: client.profileId ? String(client.profileId) : String(client.profileIdPendingSignature),
                    })
                    .pipe(map((resp: any) => this.serializer.fromJsonArray(resp, ValidModel)))
            )
        );
    }

    setFee(contractCode: IdInterface, fee: FeeInterface): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.set_fee).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        PortfolioCode: String(contractCode),
                        ModelPortfolioId: String(fee.modelId),
                        InitialInvestment: fee.investment,
                    })
                    .pipe(map((_) => true))
            )
        );
    }

    setTransfer(transfer: TransferInterface): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.set_transfer).pipe(
            switchMap((url) => this.http.post(url, this.serializer.toJson(transfer)).pipe(map((_) => true)))
        );
    }

    signDisinvestment(
        contractCode: IdInterface,
        signatories: OrderClientInterface[],
        disinvestment: DisinvestmentInterface
    ): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.sign_disinvestment).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        ContractCode: String(contractCode),
                        ClientSignatures: signatories.map((signatory) => ({
                            ClientCode: String(signatory.clientCode),
                            SignatureType: String(signatory.signatureType),
                        })),
                        WithdrawalData: this.serializer.toJson(disinvestment),
                    })
                    .pipe(map((resp: any) => mapCommonResponse(resp)))
            )
        );
    }

    signModel(contractCode: IdInterface, signatories: OrderClientInterface[], modelId: IdInterface): Observable<boolean> {
        return of(AppConfig.settings.services.base + environment.services.paths.sign_model).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        ContractCode: String(contractCode),
                        ClientSignatures: signatories.map((signatory) => ({
                            ClientCode: String(signatory.clientCode),
                            SignatureType: String(signatory.signatureType),
                        })),
                        ModelPortfolioId: String(modelId),
                    })
                    .pipe(map((resp: any) => mapCommonResponse(resp)))
            )
        );
    }

    signTransfer(contractCode: IdInterface, signatories: OrderClientInterface[], transfer: TransferInterface): Observable<boolean> {
        let step = this.localStorage.step;
        let isInitialTransfer = false;
        if (step === 13){
            isInitialTransfer = true;
        }



        return of(AppConfig.settings.services.base + environment.services.paths.sign_transfer).pipe(
            switchMap((url) =>
                this.http
                    .post(url, {
                        ContractCode: String(contractCode),
                        ClientSignatures: signatories.map((signatory) => ({
                            ClientCode: String(signatory.clientCode),
                            SignatureType: String(signatory.signatureType),
                        })),
                        ContributionData: this.serializer.toJson(transfer),
                        IsInitialTransfer: isInitialTransfer,
                    })
                    .pipe(map((resp: any) => mapCommonResponse(resp)))
            )
        );
    }
}
